"use client";
import React, { useCallback } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useClientMounted } from "@/hooks/useClientMounted";
import { Asset } from "@/hooks/useAssets";
import Star from "lucide-react/dist/esm/icons/star";
import Download from "lucide-react/dist/esm/icons/download";
import ShoppingBag from "lucide-react/dist/esm/icons/shopping-bag";
import Eye from "lucide-react/dist/esm/icons/eye";
import ShoppingCart from "lucide-react/dist/esm/icons/shopping-cart";
import Zap from "lucide-react/dist/esm/icons/zap";
import Flame from "lucide-react/dist/esm/icons/flame";
import Heart from "lucide-react/dist/esm/icons/heart";
import { Badge } from "@/components/ui/badge";
import { AssetThumb } from "@/components/asset/AssetThumb";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SellerBadge } from "@/components/ui/seller-badge";
import { EarlyFounderBadge } from "@/components/ui/early-founder-badge";
import { SubscriptionBadge } from "@/components/ui/subscription-badge";
import { usePublicTier } from "@/hooks/usePublicTier";
import { Button } from "@/components/ui/button";
import { BrowseProductCardSkeleton } from "@/components/browse/BrowseProductGridSkeleton";
import { useAddToCart } from "@/hooks/useCart";
import { useProfile } from "@/contexts/ProfileContext";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { useWishlistToggle } from "@/hooks/useWishlistToggle";
import { isPwywAsset } from "@/lib/pwyw";
import { listingPrice } from "@/lib/listing-price";
import { AssetPrice } from "@/components/asset/AssetPrice";
import { DiscountBadge } from "@/components/asset/DiscountBadge";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { fetchAssetDetail } from "@/hooks/useAssetDetail";
import { queryKeys } from "@/lib/query-keys";
import { track } from "@/lib/analytics";

interface BrowseAssetCardProps {
  asset: Asset;
  isTrending?: boolean;
  isFounderProduct?: boolean;
  isSponsored?: boolean;
  campaignId?: string;
  onSponsoredClick?: (campaignId: string) => void;
  position?: number;
}

export function BrowseAssetCard({ asset, isTrending, isFounderProduct, isSponsored, campaignId, onSponsoredClick }: BrowseAssetCardProps) {
  const router = useRouter();
  const clientMounted = useClientMounted();
  const { profile } = useProfile();
  const { data: sellerTier } = usePublicTier(asset.seller?.id);
  const { openAuthModal } = useAuthModal();
  const addToCart = useAddToCart();
  const queryClient = useQueryClient();

  // Prefetch asset detail on hover
  const handlePrefetch = useCallback(() => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.assets.detail(asset.slug),
      queryFn: () => fetchAssetDetail(asset.slug),
      staleTime: 60_000,
    });
  }, [asset.slug, queryClient]);
  const { isInWishlist, isToggling: isTogglingWishlist, toggle: toggleWishlist } = useWishlistToggle({
    assetId: asset.id,
    assetTitle: asset.title,
    price: listingPrice(asset).amount,
  });

  // Pay-what-you-want can't be carted or bought from a card: the buyer has to
  // pick an amount (or $0, when there's no minimum) on the asset page.
  const isPwyw = isPwywAsset(asset);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Pay-what-you-want needs a deliberate amount choice — send the buyer to the
    // asset page (its amount input, suggested price, minimum, and free option)
    // instead of silently carting an amountless item that dead-ends at checkout.
    if (isPwyw) {
      router.push(`/asset/${asset.slug}`);
      return;
    }

    if (!profile) {
      openAuthModal({
        message: "Add to cart",
        intent: { kind: "cart", assetId: asset.id, assetSlug: asset.slug },
        context: { title: asset.title, thumbnail: asset.primary_image, isFree: asset.is_free },
      });
      return;
    }

    track({ event: "browse_add_to_cart", asset_id: asset.id });
    addToCart.mutate(asset.id);
  };

  const handleBuyNow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // PWYW: always route to the asset page to choose the amount.
    if (isPwyw) {
      router.push(`/asset/${asset.slug}`);
      return;
    }

    if (!profile) {
      openAuthModal({
        message: asset.is_free ? "Download this asset" : "Buy this asset",
        intent: asset.is_free
          ? { kind: "download", assetId: asset.id, assetSlug: asset.slug, assetTitle: asset.title }
          : { kind: "buy", assetId: asset.id, assetSlug: asset.slug, assetTitle: asset.title },
        context: { title: asset.title, thumbnail: asset.primary_image, isFree: asset.is_free },
      });
      return;
    }

    // Free assets: navigate directly to the asset page for download
    if (asset.is_free) {
      router.push(`/asset/${asset.slug}`);
      return;
    }

    // Paid assets: add to cart and navigate to cart
    addToCart.mutate(asset.id, {
      onSuccess: () => router.push("/cart"),
      onError: (error) => {
        if (error.message === "Already in cart") router.push("/cart");
      },
    });
  };

  const handleCardClick = () => {
    if (isSponsored && campaignId && onSponsoredClick) {
      onSponsoredClick(campaignId);
      track({ event: "sponsored_asset_clicked", campaign_id: campaignId, asset_id: asset.id });
    }
    router.push(`/asset/${asset.slug}`);
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={handleCardClick}
      onMouseEnter={handlePrefetch}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } }}
      aria-label={`View ${asset.title}`}
      className={cn(
        "group relative block overflow-hidden rounded-xl border bg-card hover-lift cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
        // Neutral border for every card — trending / founder / sponsored are
        // signalled by their badges, not coloured outlines. The amber/gold
        // (warning/accent) borders read as noisy "yellow outlines" on the grid.
        "border-border hover:border-primary/40",
      )}
    >
      {/* Image */}
      <AssetThumb
        src={asset.primary_image}
        alt={asset.title}
        title={asset.title}
        hoverZoom
        fallbackClassName="text-5xl"
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Sponsored Badge - Top Right */}
        {isSponsored && (
          <Badge className="absolute right-2.5 top-2.5 bg-primary/90 backdrop-blur-sm text-primary-foreground text-2xs font-mono uppercase tracking-widest border-0 gap-1 z-10">
            <Zap className="h-3 w-3" />
            Sponsored
          </Badge>
        )}

        {/* Badges */}
        <div className="absolute left-2.5 top-2.5 flex flex-wrap gap-1.5">
          {isTrending && (
            <Badge className="bg-warning/90 backdrop-blur-sm text-warning-foreground border-0 font-mono text-2xs uppercase tracking-widest gap-1">
              <Flame className="h-3 w-3 fill-warning-foreground/70" />
              Trending
            </Badge>
          )}
          {/* The price row already says Free / Pay what you want; the image
              only carries the discount. A sale depends on `Date`, so it waits
              for mount to keep SSR and the first client paint identical. */}
          {clientMounted && <DiscountBadge asset={asset} />}
          {asset.featured && !isTrending && (
            <Badge className="border-0 bg-foreground/90 font-mono text-2xs uppercase tracking-widest text-background backdrop-blur-sm">
              Editor&apos;s pick
            </Badge>
          )}
        </div>

        {/* Category badge */}
        {asset.category && !isSponsored && (
          <Badge 
            variant="secondary" 
            className="absolute right-2.5 top-2.5 bg-card/80 backdrop-blur-sm border-border/50 font-mono text-2xs uppercase tracking-widest"
          >
            {asset.category.name}
          </Badge>
        )}

        {/* Stats overlay */}
        <div className="absolute bottom-2.5 left-2.5 flex gap-1.5">
          <div className="flex items-center gap-1 rounded-md bg-card/90 backdrop-blur-sm px-2 py-1 text-label border border-border/50">
            <ShoppingBag className="h-3 w-3 text-primary" />
            <span className="font-mono">{asset.download_count}</span>
          </div>
          <div className="flex items-center gap-1 rounded-md bg-card/90 backdrop-blur-sm px-2 py-1 text-label border border-border/50">
            <Eye className="h-3 w-3 text-primary" />
            <span className="font-mono">{asset.view_count}</span>
          </div>
        </div>
      </AssetThumb>

      {/* Content */}
      <div className="relative p-3.5 space-y-3">
        {/* Title & Rating */}
        <div className="space-y-2">
          <h3 className="font-display font-semibold text-foreground line-clamp-1 group-hover:text-primary transition-colors">
            {asset.title}
          </h3>
          <div className="flex items-center gap-2">
            {asset.avg_rating !== null ? (
              <>
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-warning/10">
                  <Star className="h-3 w-3 fill-warning text-warning" />
                  <span className="text-xs font-semibold text-warning">{asset.avg_rating.toFixed(1)}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  ({asset.review_count})
                </span>
              </>
            ) : (
              <span className="text-xs text-muted-foreground">No reviews yet</span>
            )}
          </div>
        </div>

        {/* Seller */}
        {asset.seller && (
          <Link
            href={`/store/${asset.seller.username || asset.seller.id}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <Avatar className="h-6 w-6 border border-border/50">
              <AvatarImage src={asset.seller.avatar_url || undefined} />
              <AvatarFallback className="text-xs bg-muted font-display">
                {asset.seller.display_name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm text-muted-foreground truncate">
              {asset.seller.display_name || "Unknown"}
            </span>
            <SellerBadge verified={asset.seller.seller_verified || false} size="sm" />
            {sellerTier && sellerTier !== "free" && <SubscriptionBadge tier={sellerTier} size="sm" />}
            {isFounderProduct && <EarlyFounderBadge size="sm" showTooltip={false} />}
          </Link>
        )}

        {/* Price & Actions */}
        <div className="pt-2.5 border-t border-border/30 flex items-center justify-between gap-2">
          <div>
            <AssetPrice asset={asset} className="text-lg font-display font-bold text-foreground" />
          </div>
          <div className="flex gap-1.5">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => toggleWishlist(e)}
              disabled={isTogglingWishlist}
              aria-label={isInWishlist ? `Remove ${asset.title} from wishlist` : `Add ${asset.title} to wishlist`}
              className="h-9 w-9 p-0 hover:bg-primary/5"
            >
              <Heart className={cn("h-4 w-4 transition-colors", isInWishlist && "fill-destructive text-destructive")} />
            </Button>
            {!asset.is_free && !isPwyw && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleAddToCart}
                disabled={addToCart.isPending}
                aria-label={`Add ${asset.title} to cart`}
                className="h-9 w-9 p-0 border-border/50 hover:border-primary/50 hover:bg-primary/5"
              >
                <ShoppingCart className="h-4 w-4" />
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleBuyNow}
              disabled={!asset.is_free && !isPwyw && addToCart.isPending}
              className="h-9 px-4 font-semibold"
            >
              {asset.is_free ? (
                <>
                  <Download className="h-4 w-4 mr-1" />
                  Download Free
                </>
              ) : isPwyw ? (
                "Choose amount"
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-1" />
                  Buy
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function BrowseAssetCardSkeleton({ index = 0 }: { index?: number }) {
  return <BrowseProductCardSkeleton index={index} />;
}

// Memoize so bulk list re-renders during search typing don't re-render every card.
// Only compare the fields that affect visible output; auth/cart/wishlist state changes
// already bypass the memo since they come from context hooks inside the component.
export const MemoizedBrowseAssetCard = React.memo(
  BrowseAssetCard,
  (prev, next) =>
    prev.asset.id === next.asset.id &&
    // Price fields render on the card; comparing only the id kept showing a
    // stale price (or a sale that had ended) after a refetch.
    prev.asset.price === next.asset.price &&
    prev.asset.is_free === next.asset.is_free &&
    prev.asset.pricing_type === next.asset.pricing_type &&
    prev.asset.pwyw_minimum === next.asset.pwyw_minimum &&
    prev.asset.sale_price === next.asset.sale_price &&
    prev.asset.sale_starts_at === next.asset.sale_starts_at &&
    prev.asset.sale_ends_at === next.asset.sale_ends_at &&
    prev.isTrending === next.isTrending &&
    prev.isFounderProduct === next.isFounderProduct &&
    prev.isSponsored === next.isSponsored &&
    prev.campaignId === next.campaignId,
);
